import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useUser } from "@/lib/user";
import { api, HttpError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight, Lightbulb, Hammer, Rocket, TrendingUp } from "lucide-react";

const STAGES = [
  { value: "idea", label: "Idea", desc: "Sketching, daydreaming.", Icon: Lightbulb },
  { value: "building", label: "Building", desc: "Code or no-code in flight.", Icon: Hammer },
  { value: "launched", label: "Launched", desc: "Live with paying users.", Icon: Rocket },
  { value: "scaling", label: "Scaling", desc: "Growing past PMF.", Icon: TrendingUp },
];

export default function OnboardingPage() {
  const [, navigate] = useLocation();
  const { user, patchUser } = useUser();
  const [stage, setStage] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) navigate("/");
  }, [user, navigate]);

  if (!user) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!stage) {
      setError("Pick a stage to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const updated = await api.patchUser(user.id, {
        startup_stage: stage,
        startup_description: description.trim() || null,
      });
      patchUser(updated);
      navigate("/dashboard");
    } catch (e) {
      setError(e instanceof HttpError ? "Couldn't save. Try again." : "Network error.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleSkip() {
    navigate("/dashboard");
  }

  return (
    <main data-testid="onboarding-page" className="min-h-screen px-6 sm:px-10 lg:px-20 py-16">
      <div className="max-w-2xl mx-auto">
        <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-amber-300">
          step 1 of 1 · onboarding
        </p>
        <h1 data-testid="onboarding-title" className="mt-3 text-3xl md:text-4xl font-semibold text-zinc-100 tracking-tight">
          Tell us where you are, {user.name?.split(" ")[0] || "Founder"}.
        </h1>
        <p className="mt-3 text-zinc-500 max-w-lg">
          Two questions, ten seconds. This sharpens the AI mentor's personalization across every quest.
        </p>

        <form onSubmit={handleSubmit} className="mt-12 space-y-10">
          {/* Stage */}
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-4">
              Your stage
            </label>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="stage-options">
              {STAGES.map(({ value, label, desc, Icon }) => {
                const selected = stage === value;
                return (
                  <button
                    type="button"
                    key={value}
                    data-testid={`stage-option-${value}`}
                    onClick={() => setStage(value)}
                    className={`text-left rounded-xl border p-4 transition-all ${
                      selected
                        ? "border-amber-400 bg-amber-500/[0.08] shadow-lg shadow-amber-900/20"
                        : "border-zinc-800 bg-zinc-950/40 hover:border-zinc-700 hover:bg-zinc-950/60"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`flex h-8 w-8 items-center justify-center rounded-md ${selected ? "bg-amber-500/20 text-amber-300" : "bg-zinc-900 text-zinc-500"}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <span className={`font-semibold ${selected ? "text-amber-300" : "text-zinc-200"}`}>
                        {label}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-zinc-500 ml-11">{desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-3">
              Your startup, in one sentence (optional)
            </label>
            <textarea
              data-testid="description-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="An AI mentor that turns 35 chapters of a founder book into 13 Socratic quests."
              rows={3}
              className="w-full rounded-xl border border-amber-500/15 bg-zinc-950/60 text-zinc-100 placeholder:text-zinc-600 p-4 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-500/40"
              disabled={submitting}
            />
          </div>

          {error && <p className="text-xs text-red-400" data-testid="onboarding-error">{error}</p>}

          <div className="flex items-center gap-4">
            <Button
              type="submit"
              data-testid="onboarding-submit-btn"
              disabled={submitting}
              className="h-11 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold gap-2"
            >
              {submitting ? "Saving…" : "Continue to dashboard"}
              <ArrowRight className="h-4 w-4" />
            </Button>
            <button
              type="button"
              data-testid="onboarding-skip-btn"
              onClick={handleSkip}
              className="text-sm text-zinc-500 hover:text-zinc-300"
            >
              Skip for now
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
